import { TileData } from '../types'

/**
 * Fisher-Yates shuffle algorithm
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

/**
 * Generate tiles from an image
 */
export function generateTiles(
  image: HTMLImageElement,
  gridSize: number,
  tileWidth: number,
  tileHeight: number
): TileData[] {
  const tiles: TileData[] = []
  const sourceWidth = image.width / gridSize
  const sourceHeight = image.height / gridSize

  // Create tiles in order
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const id = `tile-${row}-${col}`
      tiles.push({
        id,
        imageData: image,
        sourceX: col * sourceWidth,
        sourceY: row * sourceHeight,
        sourceWidth,
        sourceHeight,
        currentPosition: { x: 0, y: 0 }, // Will be set during shuffle
        originalPosition: { x: col * tileWidth, y: row * tileHeight },
        gridX: col,
        gridY: row,
      })
    }
  }

  // Shuffle tiles and assign shuffled positions
  const shuffledTiles = shuffleArray(tiles)
  shuffledTiles.forEach((tile, index) => {
    const row = Math.floor(index / gridSize)
    const col = index % gridSize
    tile.currentPosition = { x: col * tileWidth, y: row * tileHeight }
  })

  return shuffledTiles
}

/**
 * Check if two tiles are adjacent (for merging)
 */
export function areAdjacent(tile1: TileData, tile2: TileData, tileWidth: number, tileHeight: number): boolean {
  const dx = Math.abs(tile1.currentPosition.x - tile2.currentPosition.x)
  const dy = Math.abs(tile1.currentPosition.y - tile2.currentPosition.y)

  // Adjacent if tiles are exactly one tile width/height apart in one direction
  // and aligned in the other direction
  return (
    (dx === tileWidth && dy === 0) || // Horizontal neighbors
    (dx === 0 && dy === tileHeight)   // Vertical neighbors
  )
}

/**
 * Check if tiles are in correct position relative to each other
 */
export function areInCorrectRelativePosition(tile1: TileData, tile2: TileData): boolean {
  const originalDx = tile1.originalPosition.x - tile2.originalPosition.x
  const originalDy = tile1.originalPosition.y - tile2.originalPosition.y
  const currentDx = tile1.currentPosition.x - tile2.currentPosition.x
  const currentDy = tile1.currentPosition.y - tile2.currentPosition.y

  return originalDx === currentDx && originalDy === currentDy
}

/**
 * Check if all tiles are in their correct positions
 */
export function checkWinCondition(tiles: TileData[]): boolean {
  return tiles.every(tile =>
    tile.currentPosition.x === tile.originalPosition.x &&
    tile.currentPosition.y === tile.originalPosition.y
  )
}
